import { Component } from '@angular/core';
import { IonicPage, NavController, NavParams, Platform } from 'ionic-angular';
import { OperationService } from '../../../services/operations/operation/operation.service';
import { Entry, GapOverlapValidationRow, HierarchyCollisionSection, HierarchyTableGroup } from '../vis.models';
import { cloneDeep } from 'lodash';
import { DailyLogOperationInstance } from '../../../models/projects/operation.models';
import { DailyLogValidationService } from '../../../services/daily-log/daily-log-validation/daily-log-validation.service';
import { OperationDetailsEditPage } from '../../operations/operation-details-edit/operation-details-edit';
import { OperationSaveService } from '../../../services/operations/operation-save/operation-save.service';
import { DailyLogService } from '../../../services/daily-log/daily-log.service';
import { AppData } from '../../../models/app-data/app-data';
import { AppDataSyncService } from '../../../services/app-data-sync/app-data-sync.service';
import { DateService } from '../../../services/date/date.service';
import { PtLuxonDate } from '../../../models/projects/date.models';
import { OperationTimeService } from '../../../services/operations/operation-time/operation-time.service';
import { LockedDayService } from '../../../services/locked-day/locked-day.service';
import { LocalSaveService } from '../../../services/local/local-save.service';
import { LocalOperationSaveService } from '../../../services/local/operation/local-operation-save.service';
import { TimeZoneService } from '../../../services/time-zone/time-zone.service';
import { AppConfig } from '../../../app/app.config';

@IonicPage()
@Component({
  selector: 'page-contractual-hierarchy',
  templateUrl: 'contractual-hierarchy.html',
})
export class ContractualHierarchyPage {
  config = AppConfig.settings;
  isWeb: boolean;
  rows: GapOverlapValidationRow[] = [];
  instances: DailyLogOperationInstance[] = [];
  collisions: HierarchyCollisionSection[] = [];
  groups: HierarchyTableGroup[] = [];
  dailyLogDate: PtLuxonDate;
  endOfDay: PtLuxonDate;
  loading: boolean;
  colors: string[] = [
    'salmon',
    'lightyellow',
    'thistle',
    'palegreen',
    'paleturquoise',
    'antiquewhite',
    'burlywood'
  ];

  constructor(
    public navParams: NavParams,
    public platform: Platform,
    public operationService: OperationService,
    public dailyLogValidatorService: DailyLogValidationService,
    public operationSaveService: OperationSaveService,
    public dailyLogService: DailyLogService,
    public navCtrl: NavController,
    public appDataSyncService: AppDataSyncService,
    private dateService: DateService,
    private operationTimeService: OperationTimeService,
    private lockedDayService: LockedDayService,
    private localSaveService: LocalSaveService,
    private localOperationSaveService: LocalOperationSaveService,
    private timeZoneService: TimeZoneService,
  ) {
    this.loading = true;

  }

  async reloadInstances() {
    this.rows = [];
    this.collisions = [];
    this.groups = [];

    const ids = this.instances.map(p => p.id ? p.id : p.localId);

    this.instances = [];

    this.instances = await this.operationService.getProjectSpecificOperationInstances(ids);

    // todo - remove if ok
    // if (this.instances) {
    //   for (let i = 0; i < this.instances.length; i++) {
    //     this.instances[i] = await this.operationService.removeSystemOffsetsFromInstanceTimes(this.instances[i]);
    //   }
    // }
  }

  async ionViewDidEnter() {
    if (!!this.instances) {
      await this.reloadInstances();
    }

    this.loadData();
  }

  async ionViewDidLoad() {
    this.isWeb = this.platform.is('core');
    this.dailyLogDate = this.navParams.get('dailyLogDate');
    this.instances = this.navParams.get('instances');
  }

  async loadData() {
    let hierarchyCollisions = await this.dailyLogValidatorService.contractualHierarchy(this.instances, this.dailyLogDate);

    // filter delays from hierarchy collisions (this is also done in daily-log-validation.service.ts, after the call to contractual hierarchy)
    hierarchyCollisions = hierarchyCollisions.filter(collision =>
      collision.higherInstance.projectSpecificOperation.operationSummary.classificationName !== 'Delay'
      && collision.lowerInstance.projectSpecificOperation.operationSummary.classificationName !== 'Delay'
    );

    this.collisions = [];

    if (hierarchyCollisions.length < 1) {
      this.loading = false;
      return;
    }

    this.endOfDay = await this.dateService.getEndOfDayLocally(this.dailyLogDate.dateTime);

    let id = 1;
    for (const collision of hierarchyCollisions) {

      const newCollisionGroup = new HierarchyCollisionSection();
      newCollisionGroup.collision = collision;
      newCollisionGroup.id = id;
      newCollisionGroup.instances = [];

      const higherInstance = await this.operationService.getProjectSpecificOperationInstance(collision.higherInstanceTempId);
      const lowerInstance = await this.operationService.getProjectSpecificOperationInstance(collision.lowerInstanceTempId);

      higherInstance.startTimeToShow = await this.dateService.setTimeStringFromPtLuxonDate(higherInstance.startTime);

      if (higherInstance.endTime.dateTime) {
        higherInstance.endTimeToShow = await this.dateService.setTimeStringFromPtLuxonDate(higherInstance.endTime);
      } else {
        higherInstance.endTimeToShow = await this.dateService.setTimeStringFromDateTime(higherInstance.cancelledTime);
      }

      lowerInstance.startTimeToShow = await this.dateService.setTimeStringFromPtLuxonDate(lowerInstance.startTime);

      if (lowerInstance.endTime.dateTime) {
        lowerInstance.endTimeToShow = await this.dateService.setTimeStringFromPtLuxonDate(lowerInstance.endTime);
      } else {
        lowerInstance.endTimeToShow = await this.dateService.setTimeStringFromDateTime(lowerInstance.cancelledTime);
      }

      newCollisionGroup.instances.push(lowerInstance);
      newCollisionGroup.instances.push(higherInstance);
      id++;

      newCollisionGroup.minTime = lowerInstance.startTime.getDate() < higherInstance.startTime.getDate()
        ? lowerInstance.startTime
        : higherInstance.startTime;

      newCollisionGroup.maxTime = lowerInstance.endTime.getDate() > higherInstance.endTime.getDate()
        ? lowerInstance.endTime
        : higherInstance.endTime;

      if (!newCollisionGroup.maxTime) {
        newCollisionGroup.maxTime = this.endOfDay;
      }

      newCollisionGroup.title = `Between
       ${await this.dateService.setTimeStringFromPtLuxonDate(newCollisionGroup.minTime)} and
       ${await this.dateService.setTimeStringFromPtLuxonDate(newCollisionGroup.maxTime)} there are contractual hierarchy collisions`;

      this.collisions.push(newCollisionGroup);
      const entries = await this.tempResolve(newCollisionGroup.id);

      const group = new HierarchyTableGroup();
      group.collision = newCollisionGroup;
      group.resolvedEntries = [];
      group.resolvedEntries.push(entries);
      this.groups.push(group);
    }

    this.loading = false;
  }

  async edit(id: number) {
    const instance = await this.operationService.getProjectSpecificOperationInstance(id);
    this.navCtrl.push(OperationDetailsEditPage, {instance: instance}, {animation: 'ios-transition'});
  }

  async tempResolve(id: number): Promise<Entry[]> {
    const collision = cloneDeep(this.collisions.find(p => p.id === id));

    if (!collision) {
      return;
    }

    const instances = await this.dailyLogValidatorService.resolveContractualHierarchyCollision(collision.collision, true);

    let entries: Entry[] = [];

    for (const instance of instances) {
      instance.startTimeToShow = await this.dateService.setTimeStringFromPtLuxonDate(instance.startTime);
      instance.endTimeToShow = await this.dateService.setTimeStringFromPtLuxonDate(instance.endTime);

      if (!instance.switchTimes || instance.switchTimes.length === 0) {
        const entry = new Entry();
        entry.content = !!instance.projectSpecificOperation.name
          ? instance.projectSpecificOperation.name
          : instance.projectSpecificOperation.operationSummary.name;
        entry.contractualPriority = instance.projectSpecificOperation.contractualPriority;
        entry.start = instance.startTime.dateTime;
        entry.startToShow = instance.startTimeToShow;
        entry.endToShow = instance.endTimeToShow;
        entry.isContractual = !instance.isConcurrentOperation;
        if (entry.startToShow !== entry.endToShow) {
          entries.push(entry);
        }
      }

      for (let i = 0; i < instance.switchTimes.length; i++) {
        instance.switchTimes[i].dateToShow = await this.dateService.setTimeStringFromPtLuxonDate(instance.switchTimes[i].date);

        const entry = new Entry();
        entry.content = !!instance.projectSpecificOperation.name
          ? instance.projectSpecificOperation.name
          : instance.projectSpecificOperation.operationSummary.name;
        entry.contractualPriority = instance.projectSpecificOperation.contractualPriority;

        if (i === 0) {
          entry.start = instance.startTime.dateTime;
          entry.startToShow = instance.startTimeToShow;
          entry.endToShow = instance.switchTimes[0].dateToShow;
          entry.isContractual = !instance.isConcurrentOperation;
        } else {
          entry.start = instance.switchTimes[i - 1].date.dateTime;
          entry.startToShow = instance.switchTimes[i - 1].dateToShow;
          entry.endToShow = instance.switchTimes[i].dateToShow;
          entry.isContractual = !instance.switchTimes[i - 1].isConcurrent;
        }

        if (entry.startToShow !== entry.endToShow) {
          entries.push(entry);
        }
      }

      if (!!instance.switchTimes && instance.switchTimes.length > 0) {
        const entry = new Entry();
        entry.content = !!instance.projectSpecificOperation.name
          ? instance.projectSpecificOperation.name
          : instance.projectSpecificOperation.operationSummary.name;
        entry.contractualPriority = instance.projectSpecificOperation.contractualPriority;
        entry.start = instance.switchTimes[instance.switchTimes.length - 1].date.dateTime;
        entry.startToShow = instance.switchTimes[instance.switchTimes.length - 1].dateToShow;
        entry.endToShow = instance.endTimeToShow;
        entry.isContractual = !instance.switchTimes[instance.switchTimes.length - 1].isConcurrent;
        if (entry.startToShow !== entry.endToShow) {
          entries.push(entry);
        }
      }
    }

    entries = entries.sort((function (a, b) {
      return a.start.toMillis() - b.start.toMillis();
    }));

    return entries;
  }

  async resolve(id: number) {
    this.loading = true;
    const collision = this.collisions.find(p => p.id === id);

    if (!collision) {
      return;
    }

    const instances = await this.dailyLogValidatorService.resolveContractualHierarchyCollision(collision.collision, true);

    for (const instance of instances) {
      instance.startTimeToShow = await this.dateService.setTimeStringFromPtLuxonDate(instance.startTime);
      instance.endTimeToShow = await this.dateService.setTimeStringFromPtLuxonDate(instance.endTime);

      if (!!instance.stepCollectionInstance) {
        for (const step of instance.stepCollectionInstance.stepInstances) {
          step.startTimeToShow = await this.dateService.setTimeStringFromPtLuxonDate(step.startTime);
          step.endTimeToShow = await this.dateService.setTimeStringFromPtLuxonDate(step.endTime);
        }
      }
    }

    this.groups = this.groups.filter(p => p.collision.id !== id);
    this.collisions = this.collisions.filter(p => p.id !== id);

    for (const instance of instances) {
      const data = new AppData();
      const instanceCopy = cloneDeep(instance);
      const steps = !instanceCopy.stepCollectionInstance ? [] : instanceCopy.stepCollectionInstance.stepInstances;
      const oldLogs = await this.dailyLogService.getDailyLogDataForOperation(instance.id ? instance.id : instance.localId);
      await this.operationService.localSaveProjectSpecificOperationInstance(instance.id ? instance.id : instance.localId, instance);
      const logs = await this.operationSaveService.saveOperation(instanceCopy, steps, oldLogs, instanceCopy, true);

      data.dailyLogOperationInstances = [instanceCopy];
      data.dailyLogRecords = logs;

      try {
        await this.appDataSyncService.syncLocalData(data);
      } catch (error) {
        this.loading = false;
        throw (error);
      }
    }

    await this.reloadInstances();
    this.loadData();
  }

  getLatestEntryId(entries: Entry[]): number {
    if (!entries) {
      return 0;
    }

    let highestId = 0;
    entries.forEach(entry => {
      if (entry.id > highestId) {
        highestId = entry.id;
      }
    });

    return highestId;

  }

  getColor(id: number): string {
    const index = Math.abs(id) % this.colors.length;
    return this.colors[index];
  }
}



// WEBPACK FOOTER //
// ./src/pages/validation/contractual-hierarchy/contractual-hierarchy.ts